import { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { SparkleIcon } from "@phosphor-icons/react";

const STATUS_MESSAGES = [
    "Analysing facial structure…",
    "Optimising lighting, symmetry, presence…",
    "Refining jawline definition…",
    "Balancing skin tone and clarity…",
    "Enhancing eye area contrast…",
    "Finalising your portrait…",
];

const EXPECTED_SECONDS = 60;
const MAX_PROGRESS = 95; // never show 100% until actually done
const MESSAGE_INTERVAL_MS = 7500;

export default function LoadingOverlay({ show }) {
    const [progress, setProgress] = useState(0);
    const [messageIndex, setMessageIndex] = useState(0);
    const startRef = useRef(null);

    useEffect(() => {
        if (!show) {
            setProgress(0);
            setMessageIndex(0);
            startRef.current = null;
            return undefined;
        }
        startRef.current = Date.now();

        const progressTimer = setInterval(() => {
            if (!startRef.current) return;
            const elapsed = (Date.now() - startRef.current) / 1000;
            // Ease out as we approach the cap so motion feels natural.
            const linear = Math.min(
                1,
                elapsed / EXPECTED_SECONDS,
            );
            const eased = 1 - Math.pow(1 - linear, 1.6);
            setProgress(Math.min(MAX_PROGRESS, eased * MAX_PROGRESS));
        }, 150);

        const messageTimer = setInterval(() => {
            setMessageIndex((i) =>
                Math.min(STATUS_MESSAGES.length - 1, i + 1),
            );
        }, MESSAGE_INTERVAL_MS);

        return () => {
            clearInterval(progressTimer);
            clearInterval(messageTimer);
        };
    }, [show]);

    return (
        <AnimatePresence>
            {show ? (
                <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 0.25 }}
                    className="fixed inset-0 z-[70] flex items-center justify-center bg-[#0A0A0C]/90 backdrop-blur-xl px-6"
                    data-testid="loading-overlay"
                >
                    <div className="w-full max-w-md">
                        {/* Scanning icon */}
                        <div className="flex justify-center mb-8">
                            <div className="relative w-16 h-16 flex items-center justify-center">
                                <motion.div
                                    className="absolute inset-0 rounded-full bg-white/5 border border-white/10"
                                    animate={{
                                        scale: [1, 1.4, 1],
                                        opacity: [0.6, 0, 0.6],
                                    }}
                                    transition={{
                                        duration: 2.4,
                                        repeat: Infinity,
                                        ease: "easeOut",
                                    }}
                                />
                                <motion.div
                                    className="absolute inset-0 rounded-full bg-white/5 border border-white/10"
                                    animate={{
                                        scale: [1, 1.4, 1],
                                        opacity: [0.6, 0, 0.6],
                                    }}
                                    transition={{
                                        duration: 2.4,
                                        repeat: Infinity,
                                        ease: "easeOut",
                                        delay: 1.2,
                                    }}
                                />
                                <div className="relative w-12 h-12 rounded-full bg-white text-black flex items-center justify-center">
                                    <SparkleIcon size={22} weight="fill" />
                                </div>
                            </div>
                        </div>

                        {/* Rotating status line */}
                        <div
                            className="text-center h-7 mb-6"
                            data-testid="loading-status"
                            aria-live="polite"
                        >
                            <AnimatePresence mode="wait">
                                <motion.p
                                    key={messageIndex}
                                    initial={{ opacity: 0, y: 6 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    exit={{ opacity: 0, y: -6 }}
                                    transition={{ duration: 0.35 }}
                                    className="font-heading text-lg sm:text-xl text-white"
                                >
                                    {STATUS_MESSAGES[messageIndex]}
                                </motion.p>
                            </AnimatePresence>
                        </div>

                        {/* Progress bar */}
                        <div
                            className="relative h-1.5 w-full rounded-full bg-white/8 overflow-hidden"
                            data-testid="loading-progress"
                            role="progressbar"
                            aria-valuemin={0}
                            aria-valuemax={100}
                            aria-valuenow={Math.round(progress)}
                        >
                            <motion.div
                                className="absolute inset-y-0 left-0 bg-white rounded-full"
                                style={{ width: `${progress}%` }}
                                transition={{ duration: 0.2 }}
                            />
                            {/* subtle shimmer */}
                            <motion.div
                                className="absolute inset-y-0 w-24 bg-gradient-to-r from-transparent via-white/60 to-transparent"
                                animate={{ x: ["-120%", "420%"] }}
                                transition={{
                                    duration: 2.8,
                                    repeat: Infinity,
                                    ease: "linear",
                                }}
                                style={{ mixBlendMode: "overlay" }}
                            />
                        </div>

                        <div className="flex items-center justify-between mt-3">
                            <span className="text-[11px] tracking-[0.2em] uppercase font-semibold text-white/40">
                                Generating
                            </span>
                            <span className="text-[11px] font-medium text-white/70 tabular-nums">
                                {Math.round(progress)}%
                            </span>
                        </div>

                        <p className="mt-6 text-center text-xs text-white/40 leading-relaxed">
                            Identity locked · high-fidelity model · this
                            takes up to a minute
                        </p>
                    </div>
                </motion.div>
            ) : null}
        </AnimatePresence>
    );
}
